/* eslint-disable */
import { useRef, useEffect, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { useMasonChat } from "@/hooks/useMasonChat";

const SUGGESTIONS = [
  "What packages do you offer?",
  "How much is The Renewal?",
  "Do you travel to Maryville?",
  "How do I book?",
];

export default function MasonAIChat() {
  const [open, setOpen] = useState(false);
  const { messages, input, setInput, loading, send } = useMasonChat();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  if (!open) {
    return (
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full bg-spa-green text-spa-cream shadow-lg hover:bg-spa-green-dark transition-all hover:scale-105 flex items-center justify-center group"
        aria-label="Open chat"
      >
        <MessageCircle className="w-6 h-6" strokeWidth={1.5} />
        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-spa-gold animate-pulse" />
      </button>
    );
  }

  return (
    <div
      data-testid="chat-widget-panel"
      className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[calc(100vh-7rem)] bg-spa-cream rounded-2xl shadow-2xl border border-spa-border flex flex-col overflow-hidden animate-fade-up"
    >
      <ChatHeader onClose={() => setOpen(false)} />

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 space-y-4 bg-spa-cream">
        {messages.map((m, i) => <ChatBubble key={m.id} m={m} index={i} />)}
        {loading && <TypingIndicator />}
      </div>

      {messages.length <= 1 && (
        <div className="px-5 pb-3 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              data-testid={`chat-suggestion-${s.slice(0, 10).replace(/\s+/g, "-")}`}
              onClick={() => send(s)}
              className="text-xs px-3 py-1.5 rounded-full border border-spa-border bg-white hover:border-spa-green hover:text-spa-green transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="p-4 border-t border-spa-border bg-white flex gap-2"
      >
        <input
          data-testid="chat-widget-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about services, pricing, booking..."
          className="flex-1 bg-spa-cream border border-spa-border rounded-full px-4 py-2.5 text-sm focus:border-spa-green focus:ring-1 focus:ring-spa-green focus:outline-none"
        />
        <button
          data-testid="chat-widget-send"
          type="submit"
          disabled={loading || !input.trim()}
          className="w-10 h-10 rounded-full bg-spa-green text-spa-cream flex items-center justify-center hover:bg-spa-green-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          aria-label="Send"
        >
          <Send className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </form>
    </div>
  );
}

const ChatHeader = ({ onClose }) => (
  <div className="bg-spa-green text-spa-cream p-5 flex items-center justify-between">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-spa-gold flex items-center justify-center">
        <Sparkles className="w-4 h-4" strokeWidth={1.5} />
      </div>
      <div>
        <p className="font-serif text-lg leading-none">Mason</p>
        <p className="text-xs text-spa-cream/70 mt-1">Wellness Assistant</p>
      </div>
    </div>
    <button
      data-testid="chat-widget-close"
      onClick={onClose}
      className="text-spa-cream/70 hover:text-spa-cream transition-colors p-1"
      aria-label="Close chat"
    >
      <X className="w-5 h-5" strokeWidth={1.5} />
    </button>
  </div>
);

const ChatBubble = ({ m, index }) => (
  <div
    data-testid={`chat-message-${m.from}-${index}`}
    className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}
  >
    <div
      className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
        m.from === "user"
          ? "bg-spa-green text-spa-cream rounded-br-md"
          : "bg-white border border-spa-border text-spa-dark rounded-bl-md"
      }`}
    >
      {m.text}
    </div>
  </div>
);

const TypingIndicator = () => (
  <div className="flex justify-start">
    <div className="bg-white border border-spa-border px-4 py-3 rounded-2xl rounded-bl-md">
      <div className="flex gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-spa-gray/60 animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-1.5 h-1.5 rounded-full bg-spa-gray/60 animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-1.5 h-1.5 rounded-full bg-spa-gray/60 animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  </div>
);
